import React from "react";
import { CustomCheckbox, ClearButton, useStyles } from "./styles";
import { DatePicker, MenuItem, TextField } from "material-ui";
import { ListGridFilterMenu } from "./list-grid-filter-menu";
import { FormControlLabel } from "@material-ui/core";
import type { IPromotionsListFilters } from "../interfaces";
import SearchIcon from "components/icons/search";
import { DATATEST } from "constants/integration";
import { formatMessage } from "lib/globalize-util";
import { CreateButton } from "../styles";

export interface IListTableFilters {
    isOpen: boolean;
    selectedFilters: IPromotionsListFilters;
    allUsers: string[];
    onFilterChange(filters: IPromotionsListFilters): void;
}

export const ListTableFilters: React.FC<IListTableFilters> = ({
    isOpen,
    selectedFilters,
    onFilterChange,
    allUsers,
}) => {
    const classes = useStyles();

    const [createdBySearchResult, setCreatedBySearchResult] = React.useState<string[]>(allUsers);
    const [createdBySearchValue, setCreatedBySearchValue] = React.useState<string>("");
    const [dateRange, setDateRange] = React.useState<{ startDate?: Date; endDate?: Date }>({});
    const [dateRangeErrors, setDateRangeErrors] = React.useState<{ startDateError?: string; endDateError?: string }>(
        {}
    );

    React.useEffect(() => {
        setCreatedBySearchResult(allUsers);
    }, [allUsers, setCreatedBySearchResult]);

    const handleMenuItemClick = React.useCallback(
        (key: string, value: string) => {
            const values = selectedFilters?.[key] ? selectedFilters[key] : [];
            if (!values.includes(value)) {
                values.push(value);
            } else {
                values.splice(values.indexOf(value), 1);
            }

            if (values.length > 0) {
                onFilterChange({ ...selectedFilters, [key]: values });
            } else {
                const updatedFilters: IPromotionsListFilters = {};
                for (const [key, value] of Object.entries(selectedFilters)) {
                    if (value.length > 0) {
                        updatedFilters[key] = value;
                    }
                }

                onFilterChange(updatedFilters);
            }
        },
        [selectedFilters, onFilterChange]
    );

    const handleClearClick = React.useCallback(() => {
        onFilterChange({});
        setDateRange({});
    }, [onFilterChange, setDateRange]);

    const getChecked = React.useCallback(
        (filter, value) => {
            return !!selectedFilters?.[filter]?.includes(value);
        },
        [selectedFilters]
    );

    const isFilterSelected = React.useCallback(
        (filter) => {
            return selectedFilters?.[filter]?.length > 0;
        },
        [selectedFilters]
    );

    const typeOptions = (
        <div key="type_filter" className={classes.filterMenu}>
            <MenuItem
                onClick={() => handleMenuItemClick("type", "PRICE_ACTION_SCOPE_LINE_ITEM")}
                className="filterMenuItem"
                data-test={DATATEST.PROMOTIONS.FILTER_TYPE_PRODUCT}
            >
                <FormControlLabel
                    onChange={() => handleMenuItemClick("type", "PRICE_ACTION_SCOPE_LINE_ITEM")}
                    onClick={(e) => e.stopPropagation()}
                    control={
                        <CustomCheckbox
                            checked={getChecked("type", "PRICE_ACTION_SCOPE_LINE_ITEM")}
                            onClick={(e) => e.stopPropagation()}
                        />
                    }
                    label="Product"
                />
            </MenuItem>
            <MenuItem
                onClick={() => handleMenuItemClick("type", "PRICE_ACTION_SCOPE_TOTAL")}
                className="filterMenuItem"
                data-test={DATATEST.PROMOTIONS.FILTER_TYPE_ORDER}
            >
                <FormControlLabel
                    onChange={() => handleMenuItemClick("type", "PRICE_ACTION_SCOPE_TOTAL")}
                    onClick={(e) => e.stopPropagation()}
                    control={
                        <CustomCheckbox
                            checked={getChecked("type", "PRICE_ACTION_SCOPE_TOTAL")}
                            onClick={(e) => e.stopPropagation()}
                        />
                    }
                    label="Order"
                />
            </MenuItem>
        </div>
    );
    const statusOptions = (
        <div key="status_filter" className={classes.filterMenu}>
            <MenuItem
                onClick={() => handleMenuItemClick("status", "active")}
                className="filterMenuItem"
                data-test={DATATEST.PROMOTIONS.FILTER_STATUS_ACTIVE}
            >
                <FormControlLabel
                    onChange={() => handleMenuItemClick("status", "active")}
                    onClick={(e) => e.stopPropagation()}
                    control={
                        <CustomCheckbox checked={getChecked("status", "active")} onClick={(e) => e.stopPropagation()} />
                    }
                    label="Active"
                />
            </MenuItem>
            <MenuItem
                onClick={() => handleMenuItemClick("status", "inactive")}
                className="filterMenuItem"
                data-test={DATATEST.PROMOTIONS.FILTER_STATUS_INACTIVE}
            >
                <FormControlLabel
                    onChange={() => handleMenuItemClick("status", "inactive")}
                    onClick={(e) => e.stopPropagation()}
                    control={
                        <CustomCheckbox
                            checked={getChecked("status", "inactive")}
                            onClick={(e) => e.stopPropagation()}
                        />
                    }
                    label="Inactive"
                />
            </MenuItem>
        </div>
    );
    const handleDateRangeChange = React.useCallback(
        (date: Date, isStartDate: boolean) => {
            const isValidDateRange = isStartDate
                ? !dateRange?.endDate || date < dateRange?.endDate
                : !dateRange?.startDate || dateRange?.startDate < date;

            if (isValidDateRange) {
                setDateRange({
                    ...dateRange,
                    ...(isStartDate ? { startDate: date } : { endDate: date }),
                });
                setDateRangeErrors({
                    ...dateRangeErrors,
                    ...{ startDateError: "", endDateError: "" },
                });
            } else {
                setDateRangeErrors({
                    ...dateRangeErrors,
                    ...(isStartDate
                        ? { startDateError: formatMessage("End date should be greater than Start date") }
                        : { endDateError: formatMessage("End date should be greater than Start date") }),
                });
            }
        },
        [dateRange, dateRangeErrors, setDateRange, setDateRangeErrors]
    );

    const submitDateRange = React.useCallback(
        (event: React.SyntheticEvent<HTMLFormElement>) => {
            event.preventDefault();
            if (
                dateRange.startDate &&
                dateRange.endDate &&
                !dateRangeErrors.startDateError &&
                !dateRangeErrors.endDateError
            ) {
                onFilterChange({ ...selectedFilters, dateRange: [dateRange.startDate, dateRange.endDate] });
            }
        },
        [dateRange, dateRangeErrors, selectedFilters, onFilterChange, setDateRangeErrors]
    );

    const startDatePicker = React.useMemo(() => {
        return (
            <DatePicker
                id="date_range_start"
                mode="portrait"
                container="inline"
                hintText="Select start date"
                hideCalendarDate
                textFieldStyle={{ padding: "5px" }}
                underlineStyle={{ width: "95%" }}
                hintStyle={{ textTransform: "none", fontSize: "12px", color: "black" }}
                errorText={dateRangeErrors?.startDateError}
                onChange={(e, date) => handleDateRangeChange(date, true)}
                defaultDate={selectedFilters?.dateRange?.[0]}
                data-test={DATATEST.PROMOTIONS.FILTER_DATE_RANGE_FROM}
            />
        );
    }, [dateRangeErrors?.startDateError, selectedFilters?.dateRange?.[0], handleDateRangeChange]);

    const endDatePicker = React.useMemo(() => {
        return (
            <DatePicker
                id="date_range_end"
                mode="portrait"
                container="inline"
                hideCalendarDate
                hintText="Select end date"
                textFieldStyle={{ padding: "5px" }}
                underlineStyle={{ width: "95%" }}
                hintStyle={{ textTransform: "none", fontSize: "12px", color: "black" }}
                errorText={dateRangeErrors?.endDateError}
                onChange={(e, date) => handleDateRangeChange(date, false)}
                defaultDate={selectedFilters?.dateRange?.[1]}
                data-test={DATATEST.PROMOTIONS.FILTER_DATE_RANGE_TO}
            />
        );
    }, [dateRangeErrors?.endDateError, selectedFilters?.dateRange?.[1], handleDateRangeChange]);

    const dateRangeOptions = React.useMemo(() => {
        return (
            <form onSubmit={submitDateRange}>
                <div className={classes.filterMenu} style={{ padding: "10px" }}>
                    {formatMessage("From")}
                    {startDatePicker}
                    {formatMessage("To")}
                    {endDatePicker}
                    <div className="filterMenuDateRangeButton">
                        <CreateButton
                            disabled={false}
                            style={{ minWidth: "auto", margin: "auto 8px auto 75%" }}
                            data-test={DATATEST.PROMOTIONS.FILTER_DATE_RANGE_SUBMIT}
                            type="submit"
                        >
                            {formatMessage("Ok")}
                        </CreateButton>
                    </div>
                </div>
            </form>
        );
    }, [startDatePicker, endDatePicker]);

    const onCreatedBySearchChange = React.useCallback(
        (e, value) => {
            const filtered = !value ? allUsers : allUsers.filter((option) => option.includes(value));
            setCreatedBySearchResult(filtered);
            setCreatedBySearchValue(value);
        },
        [createdBySearchResult, setCreatedBySearchResult, setCreatedBySearchValue]
    );

    const createdByOptions = React.useMemo(() => {
        return (
            <div className={classes.filterMenu} key={"created_by"}>
                <div className="filterMenuSearchBar">
                    <SearchIcon className="filterMenuSearchIcon" />
                    <TextField
                        id="select-created-by"
                        underlineShow={false}
                        defaultValue={createdBySearchValue}
                        className={"filterMenuTextField"}
                        onChange={onCreatedBySearchChange}
                        onKeyDown={(e) => e.stopPropagation()}
                    />
                </div>
                {createdBySearchResult.length > 0 ? (
                    createdBySearchResult.sort().map((userName, index) => (
                        <MenuItem
                            onClick={() => handleMenuItemClick("createdBy", userName)}
                            className={"filterMenuItem"}
                            data-test={`${DATATEST.PROMOTIONS.FILTER_CREATED_BY}_${index}`}
                            key={`created_by_${index}`}
                        >
                            <FormControlLabel
                                onChange={() => handleMenuItemClick("createdBy", userName)}
                                onClick={(e) => e.stopPropagation()}
                                control={
                                    <CustomCheckbox
                                        checked={getChecked("createdBy", userName)}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                }
                                label={userName}
                            />
                        </MenuItem>
                    ))
                ) : (
                    <MenuItem className={"filterMenuItem"}>{formatMessage("No results found")}</MenuItem>
                )}
            </div>
        );
    }, [createdBySearchResult, createdBySearchValue, selectedFilters]);

    const clearButton = React.useMemo(() => {
        if (selectedFilters && Object.keys(selectedFilters)?.length > 0) {
            return (
                <ClearButton onClick={handleClearClick} data-test={DATATEST.PROMOTIONS.FILTER_CLEAR}>
                    {formatMessage("Clear")}
                </ClearButton>
            );
        } else {
            return <div />;
        }
    }, [selectedFilters]);

    const getButtonLabel = React.useCallback(
        (labelName: string, key: string) => {
            const values = selectedFilters?.[key] ? selectedFilters[key] : [];
            return values.length > 0 ? `${labelName} (${values.length})` : labelName;
        },
        [selectedFilters]
    );

    const filterChildren = [
        <ListGridFilterMenu
            key={"filter_menu_type"}
            label={getButtonLabel("Type", "type")}
            isSelected={isFilterSelected("type")}
            testLabel={DATATEST.PROMOTIONS.FILTER_TYPE}
        >
            {typeOptions}
        </ListGridFilterMenu>,
        <ListGridFilterMenu
            key={"filter_menu_status"}
            label={getButtonLabel("Status", "status")}
            isSelected={isFilterSelected("status")}
            testLabel={DATATEST.PROMOTIONS.FILTER_STATUS}
        >
            {statusOptions}
        </ListGridFilterMenu>,
        <ListGridFilterMenu
            key={"filter_menu_date_range"}
            label={getButtonLabel("Date Range", "dateRange")}
            isSelected={isFilterSelected("dateRange")}
            testLabel={DATATEST.PROMOTIONS.FILTER_DATE_RANGE}
        >
            {dateRangeOptions}
        </ListGridFilterMenu>,
        <ListGridFilterMenu
            key={"filter_menu_created_by"}
            onCloseMenu={() => setCreatedBySearchResult(allUsers)}
            label={getButtonLabel("Created by", "createdBy")}
            isSelected={isFilterSelected("createdBy")}
            testLabel={DATATEST.PROMOTIONS.FILTER_CREATED_BY}
        >
            {createdByOptions}
        </ListGridFilterMenu>,
        clearButton,
    ];

    if (!isOpen) {
        return <div />;
    }

    return (
        <div className={classes.filterBox}>
            <div className={"filterGroup"}>{filterChildren.map((filter) => filter)}</div>
        </div>
    );
};
